/* LumiBrace — single source of truth for analytics.
 *
 * Rules enforced here (see docs/TRACKING.md):
 *  1. Shopify already publishes `product_added_to_cart` for every /cart/add.js
 *     Ajax call. The theme must NEVER publish it again — that is a duplicate.
 *  2. Cart *edits* (color swap, photo replace, rollback/restore) technically use
 *     add.js, but they are not a new add-to-cart. They are marked with
 *     Lumi.track.markEdit() so no GA4 add_to_cart is pushed for them, and so a
 *     Custom Pixel can filter Shopify's automatic event out (docs/TRACKING.md).
 *  3. Every GA4 event is pushed at most once per logical action and always
 *     resets `ecommerce` first.
 */
(function () {
  'use strict';

  var EDIT_WINDOW_MS = 10000;
  var EDIT_KEY = 'lumi_cart_edit_until';
  var CURRENCY = 'ILS';

  function now() { return Date.now(); }

  function ss(action, key, value) {
    try {
      if (action === 'get') return sessionStorage.getItem(key);
      if (action === 'set') return sessionStorage.setItem(key, value);
      if (action === 'del') return sessionStorage.removeItem(key);
    } catch (_) { return null; }
  }

  function dl() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function push(event, ecommerce) {
    try {
      var layer = dl();
      layer.push({ ecommerce: null });
      layer.push({ event: event, ecommerce: ecommerce });
    } catch (e) { console.warn('[lumi/track] dataLayer push failed', e); }
  }

  function money(v) {
    return parseFloat(String(v == null ? 0 : v).replace(/[^\d.]/g, '')) || 0;
  }

  // Cart lines ALWAYS come from /cart.js, where every price is in agorot (cents).
  // The old "> 1000 ? /100" heuristic reported a 9.90 ILS line as 990 ILS, so the
  // conversion is now unconditional — one source of truth, no guessing.
  function linePrice(line) {
    var cents = money(line && (line.final_price != null ? line.final_price : line.price));
    return Math.round(cents) / 100;
  }

  // Round to agorot so GA4/Meta never receive 258.99999999 style values.
  function round2(n) { return Math.round((money(n) + Number.EPSILON) * 100) / 100; }

  // Empty fields are omitted, not sent as '' — blank values pollute catalog
  // matching in Meta and show up as empty dimensions in GA4.
  function item(o) {
    var out = {
      item_id: String(o.id || ''),
      item_name: o.name || 'LumiBrace',
      price: round2(o.price),
      quantity: o.quantity || 1
    };
    var variant = o.variant == null ? '' : String(o.variant).trim();
    if (variant) out.item_variant = variant;
    return out;
  }

  var fired = {};
  function once(key, fn) {
    if (fired[key]) return false;
    fired[key] = true;
    fn();
    return true;
  }

  var Track = {
    /** Mark the next few seconds of cart writes as an EDIT, not a new add. */
    markEdit: function () {
      window.__lumiCartEdit = now() + EDIT_WINDOW_MS;
      ss('set', EDIT_KEY, String(window.__lumiCartEdit));
    },
    clearEdit: function () {
      window.__lumiCartEdit = 0;
      ss('del', EDIT_KEY);
    },
    isEdit: function () {
      var until = window.__lumiCartEdit || parseInt(ss('get', EDIT_KEY) || '0', 10);
      return !!until && now() < until;
    },
    /** Run an edit-flavoured cart write with the flag held for its duration. */
    edit: function (fn) {
      Track.markEdit();
      var p;
      try { p = fn(); } catch (e) { Track.clearEdit(); throw e; }
      if (p && typeof p.then === 'function') {
        return p.then(
          function (r) { setTimeout(Track.clearEdit, 1500); return r; },
          function (e) { setTimeout(Track.clearEdit, 1500); throw e; }
        );
      }
      setTimeout(Track.clearEdit, 1500);
      return p;
    },

    /* ---------------- GA4 ---------------- */

    viewItem: function (v) {
      if (!v) return;
      once('view_item', function () {
        push('view_item', {
          currency: CURRENCY,
          value: round2(v.price),
          items: [item({ id: v.id, name: 'LumiBrace ' + (v.title || ''), variant: v.title, price: v.price })]
        });
      });
    },

    /** Only for REAL adds. Silently skipped while an edit is in flight. */
    addToCart: function (v, qty) {
      if (!v || Track.isEdit()) return;
      var q = qty || 1;
      push('add_to_cart', {
        currency: CURRENCY,
        value: round2(money(v.price) * q),
        items: [item({ id: v.id, name: 'LumiBrace ' + (v.title || ''), variant: v.title, price: v.price, quantity: q })]
      });
    },

    removeFromCart: function (line) {
      if (!line || Track.isEdit()) return;
      var price = linePrice(line);
      var q = line.quantity || 1;
      push('remove_from_cart', {
        currency: CURRENCY,
        value: round2(price * q),
        items: [item({ id: line.variant_id, name: line.product_title || 'LumiBrace', variant: line.variant_title, price: price, quantity: q })]
      });
    },

    viewCart: function (cart) {
      if (!cart) return;
      once('view_cart', function () { push('view_cart', Track.cartEcommerce(cart)); });
    },

    beginCheckout: function (cart) {
      if (!cart) return;
      once('begin_checkout', function () { push('begin_checkout', Track.cartEcommerce(cart)); });
    },

    cartEcommerce: function (cart) {
      var items = (cart.items || []).map(function (l) {
        return item({ id: l.variant_id, name: l.product_title || 'LumiBrace', variant: l.variant_title, price: linePrice(l), quantity: l.quantity });
      });
      return {
        currency: cart.currency || CURRENCY,
        value: round2(money(cart.total_price) / 100),
        items: items
      };
    },


    /* -------- Shopify Customer Events (Meta / TikTok source) -------- */

    /**
     * Publish a Shopify standard event once per session, retrying until
     * Shopify.analytics exists. The session flag is only written AFTER a
     * successful publish, so a slow-loading pixel no longer loses the event.
     */
    publishOnce: function (sessionKey, eventName, payload) {
      if (ss('get', sessionKey) === '1') return;
      var tries = 0;
      (function attempt() {
        tries++;
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish(eventName, payload);
            ss('set', sessionKey, '1');
            return;
          }
        } catch (e) { console.warn('[lumi/track] publish failed', eventName, e); return; }
        if (tries < 40) setTimeout(attempt, 250); // up to ~10s
      })();
    }
  };

  window.Lumi = window.Lumi || {};
  window.Lumi.track = Track;
})();
